import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { usePermissions } from '../../contexts/PermissionContext';
import AdminSubscriptionManagement from '../AdminSubscriptionManagement/AdminSubscriptionManagement';
import AdminPackageManagement from '../AdminPackageManagement/AdminPackageManagement';
import AdminAITest from '../AdminAITest/AdminAITest';

const DynamicModule = () => {
  const { moduleName } = useParams();
  const { hasPermission, menu } = usePermissions();

  // Find the module info from the menu by route
  const moduleInfo = menu.find(item => item.route === moduleName);

  if (!moduleInfo) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Module with route "/{moduleName}" not found or you don't have permission to access it.
        </Alert>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Available routes: {menu.map(m => `/${m.route}`).join(', ')}
        </Typography>
      </Box>
    );
  }

  // Use the actual module name for permission checks (not the route name)
  const actualModuleName = moduleInfo.module;
  
  const canView = hasPermission(actualModuleName, 'view');
  const canCreate = hasPermission(actualModuleName, 'create');
  const canUpdate = hasPermission(actualModuleName, 'update');
  const canDelete = hasPermission(actualModuleName, 'delete');
  const canExport = hasPermission(actualModuleName, 'export');
  const canImport = hasPermission(actualModuleName, 'import');

  if (!canView) {
    return (
      <Box p={3}>
        <Alert severity="error">
          You don't have permission to view {actualModuleName}.
        </Alert>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Module: {actualModuleName}<br/>
          Route: /{moduleInfo.route}<br/>
          Required permission: {actualModuleName}.view
        </Typography>
      </Box>
    );
  }

  // Special handling for subscription-related modules
  if (moduleName === 'subscription') {
    return <AdminSubscriptionManagement />;
  }
  
  if (moduleName === 'packages') {
    return <AdminPackageManagement />;
  }

  if (moduleName === 'ai-test') {
    return <AdminAITest />;
  }

  const permissions = [
    { action: 'View', allowed: canView, description: `Can view ${actualModuleName} data` },
    { action: 'Create', allowed: canCreate, description: 'Can create new records' },
    { action: 'Update', allowed: canUpdate, description: 'Can edit existing records' },
    { action: 'Delete', allowed: canDelete, description: 'Can delete records' },
    { action: 'Export', allowed: canExport, description: 'Can export data' },
    { action: 'Import', allowed: canImport, description: 'Can import data' },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {actualModuleName}
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body1">
          🎉 <strong>Success!</strong> {actualModuleName} module is working with dynamic routing!
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SettingsIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">
                  Module Information
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                This component automatically detects which access control module points to this route
                and uses the correct permissions.
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                <strong>Module Name:</strong> {actualModuleName}<br/>
                <strong>Current Route:</strong> /{moduleName}<br/>
                <strong>Component:</strong> DynamicModule (auto-detected)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Your Permissions
              </Typography>
              
              <Box>
                {permissions.map((perm) => (
                  <Box key={perm.action} display="flex" alignItems="center" mb={1}>
                    <Chip
                      icon={perm.allowed ? <CheckCircleIcon /> : undefined}
                      label={perm.action}
                      color={perm.allowed ? 'success' : 'default'}
                      variant={perm.allowed ? 'filled' : 'outlined'}
                      size="small"
                      sx={{ minWidth: 80, mr: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {perm.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Next Steps
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Now that the module system is working, you can:
              </Typography>
              
              <ul>
                <li>
                  <Typography variant="body2">
                    <strong>Test Permission Changes:</strong> Go to Permissions → Role Permissions, 
                    remove the 'view' permission from this module, and see it disappear from the sidebar.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Add More Modules:</strong> Create additional modules in the Modules section 
                    and they will automatically appear here.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Create Custom Actions:</strong> Use the "Manage Actions" feature in Modules 
                    to add custom actions beyond the standard CRUD operations.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>User-Specific Permissions:</strong> Grant specific permissions to individual 
                    users using the User Permissions tab.
                  </Typography>
                </li>
              </ul>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DynamicModule;
